// Types for the eQMS surface (versions, controlled documents, review pipeline, traceability,
// doc types/tags, external documents, attachments). Mirrors the BFF JSON contracts under
// /api/projects/{pid}/{documents,versions,trace-links}, /api/documents, /api/versions,
// /api/workspaces/{wid}/{doc-types,doc-tags,external-documents}, /api/attachments.

export type WorkAreaType = "QUALITY_MANAGEMENT" | "SOFTWARE" | "CYBERSECURITY" | "AI" | "CLINICAL";
export type VersionStatus = "PLANNED" | "IN_DEV" | "RELEASED" | "SUPERSEDED";
export type DocStatus = "DRAFT" | "IN_REVIEW" | "APPROVED" | "RELEASED" | "OBSOLETE";
export type DocReviewRole = "REVIEWER" | "APPROVER";
export type DocReviewAction = "PENDING" | "APPROVED" | "REJECTED";
export type TraceEndpointType = "ISSUE" | "DOCUMENT";
export type TraceLinkType =
  | "DERIVES" | "IMPLEMENTS" | "VERIFIES" | "SATISFIES" | "REFERENCES" | "SUPERSEDES";
export type ExternalDocCategory = "STANDARD" | "REGULATION" | "GUIDANCE" | "SUPPLIER" | "OTHER";
export type ExternalDocStatus = "ACTIVE" | "SUPERSEDED" | "WITHDRAWN";
export type AttachmentOwnerType = "PROJECT" | "VERSION" | "DOCUMENT" | "ISSUE";

/** The work areas. SOFTWARE/CYBERSECURITY/AI additionally have design + test lanes. */
export const WORK_AREAS: WorkAreaType[] =
  ["QUALITY_MANAGEMENT", "SOFTWARE", "CYBERSECURITY", "AI", "CLINICAL"];
export const WORK_AREA_LABEL: Record<WorkAreaType, string> = {
  QUALITY_MANAGEMENT: "품질경영시스템",
  SOFTWARE: "소프트웨어",
  CYBERSECURITY: "사이버보안",
  AI: "AI",
  CLINICAL: "임상",
};
/** Short labels used in the eQMS nav + on issue work-area chips. */
export const WORK_AREA_SHORT_LABEL: Record<WorkAreaType, string> = {
  QUALITY_MANAGEMENT: "품질경영(QMS)",
  SOFTWARE: "소프트웨어(SW)",
  CYBERSECURITY: "사이버보안",
  AI: "AI",
  CLINICAL: "임상",
};
/** Best-effort label for a raw work-area string (issue.workAreaTypes are strings). */
export function workAreaLabel(area: string): string {
  return (WORK_AREA_SHORT_LABEL as Record<string, string>)[area] ?? area;
}
export function hasDesignAndTest(w: WorkAreaType): boolean {
  return w === "SOFTWARE" || w === "CYBERSECURITY" || w === "AI";
}

export const DOC_STATUS_LABEL: Record<DocStatus, string> = {
  DRAFT: "초안", IN_REVIEW: "검토 중", APPROVED: "승인됨", RELEASED: "릴리스", OBSOLETE: "폐기",
};
export const VERSION_STATUS_LABEL: Record<VersionStatus, string> = {
  PLANNED: "계획", IN_DEV: "개발 중", RELEASED: "릴리스", SUPERSEDED: "대체됨",
};

// ---- Document audit / comments ----
/** Mirrors backend enum dev.compath.domain.vo.DocAuditAction (keep in sync). */
export type DocAuditAction =
  | "CREATED"
  | "METADATA_UPDATED"
  | "REVISION_ADDED"
  | "REVIEW_REQUESTED"
  | "REVIEWED_APPROVE"
  | "REVIEWED_REJECT"
  | "RELEASED"
  | "REVISION_STARTED"
  | "OBSOLETED"
  | "TAGS_CHANGED"
  | "DERIVED"
  | "COMMENTED"
  | "DELETED";

/** Append-only audit trail entry for a controlled document. */
export interface AuditEvent {
  id: string;
  documentId: string;
  projectId: string;
  actorId: string;
  action: DocAuditAction;
  detail?: string | null;
  createdAt: string;
}
/** A threaded comment on a controlled document. */
export interface DocComment {
  id: string;
  documentId: string;
  authorId: string;
  body: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/** Korean labels for known audit actions; unknown actions fall back to the raw code. */
export const DOC_AUDIT_ACTION_LABEL: Record<DocAuditAction, string> = {
  CREATED: "생성",
  METADATA_UPDATED: "메타데이터 수정",
  REVISION_ADDED: "리비전 추가",
  REVIEW_REQUESTED: "검토요청",
  REVIEWED_APPROVE: "승인",
  REVIEWED_REJECT: "반려",
  RELEASED: "릴리스",
  REVISION_STARTED: "개정 시작",
  OBSOLETED: "폐기",
  TAGS_CHANGED: "태그 변경",
  DERIVED: "도출",
  COMMENTED: "코멘트",
  DELETED: "삭제",
};
export function docAuditActionLabel(action: string): string {
  return DOC_AUDIT_ACTION_LABEL[action as DocAuditAction] ?? action;
}

/** ISO-8601 → "YYYY-MM-DD HH:mm" (locale-safe: reads the ISO string directly, no timezone shift). */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  // ISO shape "2026-07-18T09:31:00Z" → date + time via slice; fall back to raw on odd input.
  const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(iso);
  return m ? `${m[1]} ${m[2]}` : iso.slice(0, 16);
}

// ---- Test cases (design/test lane) ----
/** Mirror of the backend dev.compath.domain.vo.TestCaseStatus enum — keep in sync. */
export type TestCaseStatus = "DRAFT" | "ACTIVE" | "DEPRECATED";
/** Mirror of the backend dev.compath.domain.vo.TestCasePriority enum — keep in sync. */
export type TestCasePriority = "LOW" | "NORMAL" | "HIGH" | "CRITICAL";

export const TEST_CASE_STATUS_LABEL: Record<TestCaseStatus, string> = {
  DRAFT: "초안", ACTIVE: "활성", DEPRECATED: "폐기",
};
export const TEST_CASE_PRIORITY_LABEL: Record<TestCasePriority, string> = {
  LOW: "낮음", NORMAL: "보통", HIGH: "높음", CRITICAL: "치명",
};

/** One ordered procedure step: an action and its expected result. */
export interface TestStep {
  id: string;
  sortOrder: number;
  stepProcedure: string | null;
  expectedResult: string | null;
}
/** A controlled test case (design/test lane) with its ordered procedure. */
export interface TestCase {
  id: string;
  projectId: string;
  workAreaType: WorkAreaType;
  code: string;
  title: string;
  description: string | null;
  testCriteria: string | null;
  priority: TestCasePriority;
  status: TestCaseStatus;
  tags: string[];
  categoryId: string | null;
  steps: TestStep[];
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}
export interface CreateTestCaseRequest {
  workAreaType: WorkAreaType;
  code: string;
  title: string;
  description?: string;
  testCriteria?: string;
  priority?: TestCasePriority;
  tags?: string[];
  categoryId?: string | null;
}
export interface UpdateTestCaseRequest {
  title?: string;
  description?: string;
  testCriteria?: string;
  priority?: TestCasePriority;
  tags?: string[];
  categoryId?: string | null;
}
export interface SaveStepsRequest {
  steps: { stepProcedure: string; expectedResult: string }[];
}

// ---- Versions ----
export interface Version {
  id: string;
  projectId: string;
  label: string;
  status: VersionStatus;
  plannedDate?: string | null;
  releasedAt?: string | null;
  releasedInSprintId?: string | null;
  notes: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}
export interface PlanVersionRequest { label: string; plannedDate?: string | null; notes?: string | null; }
export interface UpdateVersionRequest { label?: string | null; plannedDate?: string | null; notes?: string | null; }

// ---- Documents ----
export interface Document {
  id: string;
  projectId: string;
  workAreaType: WorkAreaType;
  targetVersionId?: string | null;
  docTypeId?: string | null;
  docNumber: string;
  title: string;
  status: DocStatus;
  summary: string;
  memo: string;
  ownerId?: string | null;
  authorId?: string | null;
  reviewerId?: string | null;
  approverId?: string | null;
  format: string;
  currentRevisionId?: string | null;
  aiSummary: string;
  aiKeyPoints: string[];
  aiSummaryAt?: string | null;
  tagIds: string[];
  derivedFromIssueIds: string[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}
export interface CreateDocumentRequest {
  workAreaType: WorkAreaType;
  docTypeId?: string | null;
  docNumber?: string | null;
  title: string;
  format?: string | null;
  targetVersionId?: string | null;
  ownerId?: string | null;
  authorId?: string | null;
}
export interface UpdateDocumentRequest {
  title?: string | null;
  summary?: string | null;
  memo?: string | null;
  workAreaType?: WorkAreaType | null;
  targetVersionId?: string | null;
  docTypeId?: string | null;
  ownerId?: string | null;
  authorId?: string | null;
  reviewerId?: string | null;
  approverId?: string | null;
}

export interface DocumentRevision {
  id: string;
  documentId: string;
  revLabel: string;
  s3Key: string;
  filename: string;
  size: number;
  changeSummary: string;
  uploadedBy?: string | null;
  toc: string;
  createdAt: string;
}
export interface UploadRevisionRequest {
  revLabel: string;
  s3Key: string;
  filename?: string | null;
  size: number;
  changeSummary?: string | null;
  toc?: string | null;
}

export interface DocReviewTask {
  id: string;
  documentId: string;
  role: DocReviewRole;
  assigneeId: string;
  requesterId?: string | null;
  action: DocReviewAction;
  comment: string;
  dueDate?: string | null;
  actedAt?: string | null;
  createdAt: string;
}
export interface RequestReviewRequest {
  reviewerIds: string[];
  approverIds: string[];
  dueDate?: string | null;
}

// ---- Traceability ----
export interface TraceLink {
  id: string;
  projectId: string;
  sourceType: TraceEndpointType;
  sourceId: string;
  targetType: TraceEndpointType;
  targetId: string;
  linkType: TraceLinkType;
  note: string;
  createdBy?: string | null;
  createdAt: string;
}
export interface CreateTraceLinkRequest {
  sourceType: TraceEndpointType;
  sourceId: string;
  targetType: TraceEndpointType;
  targetId: string;
  linkType: TraceLinkType;
  note?: string | null;
}

// ---- Catalog ----
export interface DocType {
  id: string;
  workspaceId: string;
  name: string;
  prefix: string;
  digits: number;
  sortOrder: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}
export interface CreateDocTypeRequest { name: string; prefix: string; digits: number; sortOrder: number; }

export interface DocTag {
  id: string;
  workspaceId: string;
  name: string;
  color: string;
  createdAt: string;
}
export interface CreateDocTagRequest { name: string; color?: string | null; }

// ---- External documents ----
/** One uploaded version of an external document's file. */
export interface ExternalDocRevision {
  id: string;
  externalDocumentId: string;
  revNo: number;
  filename: string;
  size: number;
  /** SHA-256 of the bytes — two revisions with the same hash hold the same file. */
  contentHash: string;
  changeSummary: string;
  uploadedBy?: string | null;
  createdAt: string;
}

export type DiffLineKind = "CONTEXT" | "ADDED" | "REMOVED" | "GAP";

/** Line numbers are 0 on the side where the line does not exist; a GAP stands for skipped context. */
export interface DiffLine {
  kind: DiffLineKind;
  oldNo: number;
  newNo: number;
  text: string;
}

/**
 * Comparison of two uploads. `textual` is false when either side is not readable text (a PDF edition
 * of a standard) — the UI then reports size/hash rather than pretending to compare lines.
 */
export interface RevisionDiff {
  from: ExternalDocRevision;
  to: ExternalDocRevision;
  textual: boolean;
  identical: boolean;
  truncated: boolean;
  added: number;
  removed: number;
  lines: DiffLine[];
}

export interface ExternalDocument {
  id: string;
  workspaceId: string;
  title: string;
  externalRef: string;
  category: ExternalDocCategory;
  issuer: string;
  edition: string;
  status: ExternalDocStatus;
  effectiveDate?: string | null;
  reviewDate?: string | null;
  link: string;
  notes: string;
  ownerId?: string | null;
  filename: string;
  s3Key: string;
  size: number;
  createdAt: string;
  updatedAt: string;
  version: number;
}
export interface CreateExternalDocumentRequest {
  title: string;
  externalRef?: string | null;
  category?: ExternalDocCategory | null;
  issuer?: string | null;
  edition?: string | null;
  effectiveDate?: string | null;
  reviewDate?: string | null;
  link?: string | null;
  notes?: string | null;
  ownerId?: string | null;
}

// ---- Attachments ----
export interface Attachment {
  id: string;
  ownerType: AttachmentOwnerType;
  ownerId: string;
  filename: string;
  s3Key: string;
  contentType: string;
  size: number;
  uploadedBy?: string | null;
  createdAt: string;
}
